import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ChevronLeft, ChevronRight, Trophy, Calendar as CalendarIcon, Settings, Filter, List, Grid3x3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Team colors for visual distinction
const TEAM_COLORS = {
  default: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' },
  academy: { bg: 'bg-teal-500/20', border: 'border-teal-500/30', text: 'text-teal-400' },
  Padantiques: { bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400' },
  Legends: { bg: 'bg-red-500/20', border: 'border-red-500/30', text: 'text-red-400' },
  Thirds: { bg: 'bg-emerald-500/20', border: 'border-green-500/30', text: 'text-emerald-400' },
  Masters: { bg: 'bg-orange-500/20', border: 'border-orange-500/30', text: 'text-orange-400' },
  Olympians: { bg: 'bg-indigo-500/20', border: 'border-indigo-500/30', text: 'text-indigo-400' },
};

export default function Calendar({ user, logout }) {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [games, setGames] = useState([]);
  const [events, setEvents] = useState([]);
  const [academySessions, setAcademySessions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [academyTeams, setAcademyTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateItems, setSelectedDateItems] = useState([]);
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    teams: [],
    types: ['game', 'event', 'academy_session'],
    statuses: ['scheduled', 'completed', 'cancelled']
  });

  const isAcademyCoach = user?.roles?.includes('academy_coach');
  const isAcademyParent = user?.roles?.includes('academy_parent');
  const isSectionMember = user?.member_type === 'section' || ['convenor', 'captain'].includes(user?.role);

  useEffect(() => {
    fetchGames();
    fetchEvents();
    fetchTeams();
    if (isAcademyCoach || isAcademyParent) {
      fetchAcademySessions();
      fetchAcademyTeams();
    }
  }, []);

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/games`, { withCredentials: true });
      setGames(response.data);
    } catch (error) {
      console.error("Error fetching games:", error);
      toast.error("Failed to load games");
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const fetchAcademySessions = async () => {
    try {
      const response = await axios.get(`${API}/academy/sessions`, { withCredentials: true });
      setAcademySessions(response.data);
    } catch (error) {
      console.error("Error fetching academy sessions:", error);
    }
  };

  const fetchAcademyTeams = async () => {
    try {
      const response = await axios.get(`${API}/academy/teams`, { withCredentials: true });
      setAcademyTeams(response.data);
    } catch (error) {
      console.error("Error fetching academy teams:", error);
    }
  };

  const getTeamColor = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return TEAM_COLORS.default;
    return TEAM_COLORS[team.name] || TEAM_COLORS.default;
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateString);
    targetDate.setHours(0, 0, 0, 0);
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filterItems = (items) => {
    return items.filter(item => {
      // Filter by type
      if (!filters.types.includes(item.type)) return false;
      
      // Filter by status (games and academy sessions)
      if ((item.type === 'game' || item.type === 'academy_session') && !filters.statuses.includes(item.status)) return false;
      
      // Filter by team (games and academy sessions)
      if ((item.type === 'game' || item.type === 'academy_session') && filters.teams.length > 0) {
        if (item.type === 'game') {
          const game = games.find(g => g.id === item.id);
          if (!game || !filters.teams.includes(game.team_id)) return false;
        } else if (item.type === 'academy_session') {
          const session = academySessions.find(s => s.id === item.id);
          if (!session || !filters.teams.includes(session.team_id)) return false;
        }
      }
      
      return true;
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getItemsForDate = (dateString) => {
    const items = [];
    
    // Find games for this date
    games.forEach(game => {
      const gameDate = new Date(game.date).toISOString().split('T')[0];
      if (gameDate === dateString) {
        const team = teams.find(t => t.id === game.team_id);
        const signups = game.signups || [];
        const isSignedUp = signups.some(s => s.user_id === user.id);
        const needsSignup = game.status === 'scheduled' && !game.signups_closed && !isSignedUp;
        
        items.push({
          type: 'game',
          title: `vs ${game.opponent}`,
          teamName: team?.name || 'Team',
          time: new Date(game.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          location: game.location,
          status: game.status,
          signedUp: isSignedUp,
          needsSignup: needsSignup,
          squadInfo: `${signups.length}${game.squad_limit ? `/${game.squad_limit}` : ''}`,
          color: getTeamColor(game.team_id),
          id: game.id,
          teamId: game.team_id,
          daysUntil: getDaysUntil(dateString)
        });
      }
    });

    // Find events for this date
    events.forEach(event => {
      const eventDate = new Date(event.date).toISOString().split('T')[0];
      if (eventDate === dateString) {
        const signups = event.signups || [];
        const isSignedUp = signups.includes(user.id);
        
        items.push({
          type: 'event',
          title: event.title,
          time: new Date(event.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          location: event.location,
          signedUp: isSignedUp,
          attendees: signups.length,
          id: event.id,
          daysUntil: getDaysUntil(dateString)
        });
      }
    });

    // Find academy sessions for this date
    academySessions.forEach(session => {
      const sessionDate = new Date(session.session_date).toISOString().split('T')[0];
      if (sessionDate === dateString) {
        const academyTeam = academyTeams.find(t => t.id === session.team_id);
        const signups = session.signups || [];
        const isSignedUp = signups.includes(user?.academy_player_id);
        
        items.push({
          type: 'academy_session',
          title: session.session_type === 'game' ? `vs ${session.opponent || 'TBD'}` : 'Training',
          teamName: session.team_name || academyTeam?.name || 'Academy',
          time: session.start_time || new Date(session.session_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          endTime: session.end_time,
          location: session.location,
          status: session.status,
          signedUp: isSignedUp,
          squadInfo: `${signups.length}${session.squad_limit ? `/${session.squad_limit}` : ''}`,
          color: TEAM_COLORS.academy,
          id: session.id,
          teamId: session.team_id,
          sessionType: session.session_type,
          daysUntil: getDaysUntil(dateString)
        });
      }
    });

    return filterItems(items);
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-28 bg-[#0a0f1a]"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const items = getItemsForDate(dateString);
      const isToday = dateString === new Date().toISOString().split('T')[0];
      const hasItems = items.length > 0;
      const isPast = new Date(dateString) < new Date(new Date().setHours(0,0,0,0));

      days.push(
        <div
          key={day}
          onClick={() => {
            if (hasItems) {
              setSelectedDate(dateString);
              setSelectedDateItems(items);
            }
          }}
          className={`h-28 border border-white/5 p-2 relative overflow-hidden ${
            isToday ? 'bg-yellow-500/10 border-2 border-yellow-500/30' : isPast ? 'bg-[#0a0f1a]' : 'bg-slate-900/50'
          } ${hasItems ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
        >
          <div className="flex justify-between items-start mb-1">
            <div className={`text-sm font-semibold ${isToday ? 'text-yellow-500 text-base' : isPast ? 'text-slate-400' : 'text-slate-300'}`}>
              {day}
            </div>
            {items.length > 0 && (
              <span className="text-xs bg-slate-700 text-slate-300 px-1.5 py-0.5 rounded-full font-medium">
                {items.length}
              </span>
            )}
          </div>
          
          {items.length > 0 && (
            <div className="space-y-1">
              {items.slice(0, 2).map((item, idx) => {
                const itemIcon = item.type === 'game' ? '⚽' : item.type === 'academy_session' ? (item.sessionType === 'game' ? '🏃' : '🎓') : '📅';
                const itemBg = item.type === 'game' 
                  ? (item.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : item.color?.bg + ' ' + item.color?.text)
                  : item.type === 'academy_session'
                  ? (item.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : item.color?.bg + ' ' + item.color?.text)
                  : 'bg-emerald-500/20 text-emerald-400';
                const itemTitle = item.type === 'game' 
                  ? `${item.teamName} ${item.title}`
                  : item.type === 'academy_session'
                  ? `${item.teamName} - ${item.title}`
                  : item.title;
                const itemDisplay = item.type === 'game' 
                  ? item.teamName.substring(0, 4)
                  : item.type === 'academy_session'
                  ? `${item.teamName.substring(0, 3)}`
                  : item.title.substring(0, 10);

                return (
                  <div
                    key={idx}
                    className={`text-xs p-1 rounded truncate flex items-center gap-1 ${itemBg}`}
                    title={itemTitle}
                  >
                    <span>{itemIcon}</span>
                    <span className="truncate flex-1">{itemDisplay}</span>
                    {item.needsSignup && <span className="text-yellow-500">●</span>}
                    {item.signedUp && <span className="text-emerald-400">✓</span>}
                  </div>
                );
              })}
              {items.length > 2 && (
                <div className="text-xs text-slate-400 font-medium">+{items.length - 2} more</div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      const dateString = day.toISOString().split('T')[0];
      const items = getItemsForDate(dateString);
      const isToday = dateString === new Date().toISOString().split('T')[0];
      
      days.push(
        <div key={i} className="border-r last:border-r-0 border-white/5 p-3 min-h-[400px]">
          <div className={`text-center mb-3 ${isToday ? 'bg-yellow-500/20 rounded-lg py-2' : ''}`}>
            <div className={`text-xs font-medium ${isToday ? 'text-yellow-500' : 'text-slate-400'}`}>
              {DAYS_OF_WEEK[i]}
            </div>
            <div className={`text-2xl font-bold ${isToday ? 'text-yellow-500' : 'text-white'}`}>
              {day.getDate()}
            </div>
          </div>
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div
                key={idx}
                onClick={() => {
                  setSelectedDate(dateString);
                  setSelectedDateItems(items);
                }}
                className={`p-2 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                  item.type === 'game'
                    ? item.status === 'cancelled'
                      ? 'border-red-500 bg-red-500/10'
                      : item.color?.border + ' ' + item.color?.bg
                    : 'border-green-500 bg-emerald-500/10'
                }`}
              >
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-sm">{item.type === 'game' ? '⚽' : '📅'}</span>
                  <span className="text-xs font-semibold truncate">{item.title}</span>
                </div>
                <div className="text-xs text-slate-400">{item.time}</div>
                {item.type === 'game' && (
                  <div className="flex gap-2 mt-1">
                    {item.signedUp && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">✓ Signed Up</span>}
                    {item.needsSignup && <span className="text-xs bg-yellow-500/20 text-yellow-500 px-1.5 py-0.5 rounded">● Sign Up</span>}
                    <span className="text-xs bg-slate-800/50 text-slate-300 px-1.5 py-0.5 rounded">{item.squadInfo}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    return days;
  };

  const renderListView = () => {
    // Get all items for current month
    const { daysInMonth, year, month } = getDaysInMonth(currentDate);
    const allItems = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const items = getItemsForDate(dateString);
      if (items.length > 0) {
        allItems.push({ date: dateString, items });
      }
    }
    
    // Sort by date
    allItems.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return (
      <div className="space-y-6">
        {allItems.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p>No games or events this month</p>
          </div>
        ) : (
          allItems.map(({ date, items }) => (
            <div key={date} className="bg-slate-900/50 rounded-lg border-2 border-white/5 p-4">
              <div className="flex items-center gap-3 mb-3 pb-3 border-b">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">{new Date(date).getDate()}</div>
                  <div className="text-xs text-slate-400 uppercase">{DAYS_OF_WEEK[new Date(date).getDay()]}</div>
                </div>
                <div>
                  <div className="font-semibold text-white">
                    {new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </div>
                  {items[0].daysUntil === 0 && <span className="text-xs bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded font-medium">Today</span>}
                  {items[0].daysUntil === 1 && <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded font-medium">Tomorrow</span>}
                  {items[0].daysUntil > 1 && items[0].daysUntil <= 7 && <span className="text-xs bg-slate-800/50 text-slate-300 px-2 py-1 rounded font-medium">In {items[0].daysUntil} days</span>}
                </div>
              </div>
              
              <div className="space-y-3">
                {items.map((item, idx) => (
                  <div
                    key={idx}
                    onClick={() => navigate(item.type === 'game' ? '/games' : '/events')}
                    className={`p-4 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow ${
                      item.type === 'game'
                        ? item.status === 'cancelled'
                          ? 'border-red-500 bg-red-500/10'
                          : item.color?.border + ' ' + item.color?.bg
                        : 'border-green-500 bg-emerald-500/10'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xl">{item.type === 'game' ? '⚽' : '📅'}</span>
                          <div>
                            <div className="font-bold text-white">{item.title}</div>
                            {item.type === 'game' && <div className="text-xs text-slate-400">{item.teamName}</div>}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                          <span>🕐 {item.time}</span>
                          <span>📍 {item.location}</span>
                          {item.type === 'game' && <span>👥 {item.squadInfo} players</span>}
                          {item.type === 'event' && <span>👥 {item.attendees} attendees</span>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        {item.type === 'game' && item.signedUp && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded font-medium">✓ Signed Up</span>}
                        {item.type === 'game' && item.needsSignup && <Button size="sm" className="bg-yellow-500/100 hover:bg-yellow-400 text-black text-xs">Sign Up</Button>}
                        {item.type === 'game' && item.status === 'cancelled' && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded font-medium">Cancelled</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const goToPreviousMonth = () => {
    if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    }
  };

  const goToNextMonth = () => {
    if (viewMode === 'week') {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0f1a]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-slate-300 font-medium">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <Button
            onClick={() => navigate("/dashboard")}
            variant="ghost"
            className="text-yellow-500 hover:text-yellow-500"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => navigate("/calendar-settings")} variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-2" />
              Sync
            </Button>
            <Button onClick={() => navigate("/games")} variant="outline" size="sm">
              <Trophy className="w-4 h-4 mr-2" />
              Games
            </Button>
            <Button onClick={() => navigate("/events")} variant="outline" size="sm">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Events
            </Button>
          </div>
        </div>

        <Card className="p-6 bg-slate-900/50 border-2 border-white/5 shadow-lg">
          {/* Calendar Controls */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white">
                {viewMode === 'week' 
                  ? `Week of ${currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                }
              </h1>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {/* View Mode Toggle */}
              <div className="flex border border-slate-700 rounded overflow-hidden">
                <Button 
                  onClick={() => setViewMode('month')} 
                  size="sm"
                  variant={viewMode === 'month' ? 'default' : 'ghost'}
                  className={`rounded-none ${viewMode === 'month' ? 'bg-yellow-500/100 text-black' : ''}`}
                >
                  <Grid3x3 className="w-4 h-4" />
                </Button>
                <Button 
                  onClick={() => setViewMode('week')} 
                  size="sm"
                  variant={viewMode === 'week' ? 'default' : 'ghost'}
                  className={`rounded-none border-x ${viewMode === 'week' ? 'bg-yellow-500/100 text-black' : ''}`}
                >
                  <CalendarIcon className="w-4 h-4" />
                </Button>
                <Button 
                  onClick={() => setViewMode('list')} 
                  size="sm"
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  className={`rounded-none ${viewMode === 'list' ? 'bg-yellow-500/100 text-black' : ''}`}
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>

              {/* Filter Toggle */}
              <Button 
                onClick={() => setShowFilters(!showFilters)} 
                variant="outline" 
                size="sm"
                className={showFilters ? 'bg-yellow-500/20' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>

              {/* Navigation */}
              <div className="flex items-center gap-2">
                <Button onClick={goToPreviousMonth} variant="outline" size="sm">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button onClick={goToToday} variant="outline" size="sm">
                  Today
                </Button>
                <Button onClick={goToNextMonth} variant="outline" size="sm">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mb-6 p-4 bg-[#0a0f1a] rounded-lg border border-white/5">
              <div className="grid md:grid-cols-3 gap-4">
                {/* Team Filter */}
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Teams</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {teams.map(team => (
                      <label key={team.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={filters.teams.includes(team.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilters({...filters, teams: [...filters.teams, team.id]});
                            } else {
                              setFilters({...filters, teams: filters.teams.filter(id => id !== team.id)});
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{team.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Type Filter */}
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Item Types</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.types.includes('game')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({...filters, types: [...filters.types, 'game']});
                          } else {
                            setFilters({...filters, types: filters.types.filter(t => t !== 'game')});
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">⚽ Games</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.types.includes('event')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({...filters, types: [...filters.types, 'event']});
                          } else {
                            setFilters({...filters, types: filters.types.filter(t => t !== 'event')});
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">📅 Events</span>
                    </label>
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">Game Status</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.statuses.includes('scheduled')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({...filters, statuses: [...filters.statuses, 'scheduled']});
                          } else {
                            setFilters({...filters, statuses: filters.statuses.filter(s => s !== 'scheduled')});
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Scheduled</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.statuses.includes('completed')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({...filters, statuses: [...filters.statuses, 'completed']});
                          } else {
                            setFilters({...filters, statuses: filters.statuses.filter(s => s !== 'completed')});
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Completed</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.statuses.includes('cancelled')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters({...filters, statuses: [...filters.statuses, 'cancelled']});
                          } else {
                            setFilters({...filters, statuses: filters.statuses.filter(s => s !== 'cancelled')});
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Cancelled</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setFilters({
                    teams: [],
                    types: ['game', 'event'],
                    statuses: ['scheduled', 'completed', 'cancelled']
                  })}
                >
                  Reset Filters
                </Button>
              </div>
            </div>
          )}

          {/* Calendar Views */}
          {viewMode === 'month' && (
            <>
              {/* Day Headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAYS_OF_WEEK.map(day => (
                  <div key={day} className="text-center font-semibold text-slate-400 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-px bg-slate-700">
                {renderCalendar()}
              </div>
            </>
          )}

          {viewMode === 'week' && (
            <div className="grid grid-cols-7 gap-px bg-slate-700 border border-white/5">
              {renderWeekView()}
            </div>
          )}

          {viewMode === 'list' && renderListView()}

          {/* Legend */}
          {viewMode !== 'list' && (
            <div className="mt-6 flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500/10 border-2 border-yellow-500/30 rounded"></div>
                <span className="text-slate-300">Today</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-emerald-400 font-bold">✓</span>
                <span className="text-slate-300">Signed Up</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-yellow-500 font-bold">●</span>
                <span className="text-slate-300">Needs Signup</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500/20 border border-red-500/30 rounded"></div>
                <span className="text-slate-300">Cancelled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-emerald-500/20 border border-green-500/30 rounded"></div>
                <span className="text-slate-300">Events</span>
              </div>
              {teams.slice(0, 3).map(team => {
                const color = getTeamColor(team.id);
                return (
                  <div key={team.id} className="flex items-center gap-2">
                    <div className={`w-4 h-4 ${color.bg} border ${color.border} rounded`}></div>
                    <span className="text-slate-300">{team.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Selected Date Details Dialog */}
        {selectedDate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedDate(null)}>
            <Card className="max-w-lg w-full m-4 p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">
                  {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h2>
                <Button onClick={() => setSelectedDate(null)} variant="ghost" size="sm">✕</Button>
              </div>

              <div className="space-y-3">
                {selectedDateItems.map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border-2 ${
                      item.type === 'game'
                        ? item.status === 'cancelled'
                          ? 'border-red-500/30 bg-red-500/10'
                          : 'border-blue-500/30 bg-blue-500/10'
                        : 'border-green-500/30 bg-emerald-500/10'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{item.type === 'game' ? '⚽' : '📅'}</span>
                          <h3 className="font-bold text-white">{item.title}</h3>
                        </div>
                        <p className="text-sm text-slate-400">🕐 {item.time}</p>
                        <p className="text-sm text-slate-400">📍 {item.location}</p>
                        {item.status === 'cancelled' && (
                          <p className="text-sm text-red-400 font-semibold mt-1">❌ Cancelled</p>
                        )}
                      </div>
                      <Button
                        onClick={() => {
                          setSelectedDate(null);
                          navigate(item.type === 'game' ? '/games' : '/events');
                        }}
                        size="sm"
                        variant="outline"
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
